import React from 'react';
import { IScoreDetail } from 'crono-fe-common/types/leadScore';
import {
  comparatorsByValueType,
  getHashedScoreDetailsConfig,
  getHashedSettingsValidationConfig,
  getHashedDateUnits,
} from 'crono-fe-common/components/LeadScore/lead-score.constants';
import { IScoreSettingsComparator } from 'crono-fe-common/components/LeadScore/lead-score.types';
import { format, isValid } from 'date-fns';
import {
  ExternalProperty,
  ExternalPropertyValueType,
} from 'crono-fe-common/types/externalProperty';
import isNil from 'lodash/isNil';
import { LeadScoreProperty } from 'crono-fe-common/types/leadScore';
import { cn } from 'utils/style-utils';
import { useLeadScoreSignalsConfig } from '../hooks/useLeadScoreSignalsConfig';

interface ILeadScoreDetailsItemProps {
  detail: IScoreDetail;
  defaultProperties: Record<string, LeadScoreProperty>;
  significantNegative?: boolean;
  significantPositive?: boolean;
  insignificant?: boolean;
  normalizedScore: number;
  score: number;
}

interface IDetailsInfo {
  label: string;
  comparator?: string;
  value?: string | number | boolean | (string | number)[] | JSX.Element;
}

const hashedSettingsValidationConfig = getHashedSettingsValidationConfig();
const hashedScoreDetailsConfig = getHashedScoreDetailsConfig();
const hashedDateUnits = getHashedDateUnits();

export const LeadScoreDetailsItem = ({
  detail,
  normalizedScore,
  score,
  defaultProperties,
  significantNegative,
  significantPositive,
  insignificant,
}: ILeadScoreDetailsItemProps) => {
  const scoreDetailType = detail.scoreDetailType;

  const { getHashedSettingsSignalsConfig } = useLeadScoreSignalsConfig();
  const hashedSettingsSignalsConfig = getHashedSettingsSignalsConfig();

  const isSignificant = significantNegative || significantPositive;

  const detailConfig =
    hashedSettingsValidationConfig[scoreDetailType] ||
    hashedSettingsSignalsConfig[scoreDetailType] ||
    hashedScoreDetailsConfig[scoreDetailType];

  function buildDetailsInfoFromProperty(
    propertyName?: string,
    valueType?: ExternalPropertyValueType,
    preferAlternateLabel = false,
  ) {
    const value = [
      detail.expectedStr?.map((str) => {
        const externalProperty = detail.externalProperty;
        const valueIdx = externalProperty?.options?.findIndex(
          (option) => option === str,
        );

        if (valueIdx === undefined || valueIdx === -1) {
          return str;
        }

        return externalProperty?.labels?.[valueIdx];
      }),
      detail.expectedNum,
      detail.expectedBoolean,
    ].find((value) => !isNil(value)) as
      | string
      | number
      | boolean
      | (string | number)[];

    const comparatorConfig = valueType
      ? comparatorsByValueType[valueType]?.comparators.find(
          (comparator: IScoreSettingsComparator) =>
            comparator.value === detail.comparator,
        )
      : undefined;
    const comparatorLabel = preferAlternateLabel
      ? (comparatorConfig?.alternateLabel ?? comparatorConfig?.label)
      : comparatorConfig?.label;

    return {
      label: propertyName ?? '',
      comparator: comparatorLabel,
      value:
        detail.comparator === 'Between' ? (
          <>
            <span className="font-semibold">{detail.expectedNumMin ?? 0}</span>{' '}
            <span className="font-light">and</span>{' '}
            <span className="font-semibold">{detail.expectedNumMax ?? 0}</span>
          </>
        ) : (
          value
        ),
    };
  }

  function getDetailsInfoFromSettingItem(): IDetailsInfo {
    const detailConfig =
      hashedSettingsValidationConfig[scoreDetailType] ||
      hashedSettingsSignalsConfig[scoreDetailType] ||
      hashedScoreDetailsConfig[scoreDetailType];

    if (scoreDetailType === 'Hiring') {
      const city = detail.data ? detail.data.split(',')[0].trim() : null;
      return {
        label: 'Company is hiring',
        value: (
          <>
            {detail.expectedStr?.map((job, idx, arr) => (
              <span className="font-semibold" key={idx}>
                {job}
                {idx < arr.length - 1 ? ', ' : ''}
              </span>
            ))}
            {city && <span className="font-light"> in {city}</span>}
          </>
        ),
      };
    }

    return {
      label: (detailConfig?.getDetailLabel(detail.data) as string) ?? '',
    };
  }

  function getDetailsInfoFromExternalPropRule(
    externalProperty: ExternalProperty,
  ): IDetailsInfo {
    return buildDetailsInfoFromProperty(
      externalProperty.publicName,
      externalProperty.valueType,
    );
  }

  function getDetailsInfoFromDefaultPropertyRule(
    defaultProperty: LeadScoreProperty,
  ): IDetailsInfo {
    return buildDetailsInfoFromProperty(
      defaultProperty?.publicName,
      defaultProperty?.valueType,
      true,
    );
  }

  let detailsInfoLabel: IDetailsInfo = {
    label: '',
    comparator: '',
    value: undefined,
  };

  let defaultPropertyKey = '';

  if (detail.accountProperty) {
    defaultPropertyKey = `account-${detail.accountProperty}`;
  } else if (detail.prospectProperty) {
    defaultPropertyKey = `prospect-${detail.prospectProperty}`;
  }

  if (detail.externalProperty) {
    detailsInfoLabel = getDetailsInfoFromExternalPropRule(
      detail.externalProperty,
    );
  }

  if (
    ![
      'AccountExternalProperty',
      'ProspectExternalProperty',
      'AccountDefaultProperty',
      'ProspectDefaultProperty',
    ].includes(detail.scoreDetailType)
  ) {
    detailsInfoLabel = getDetailsInfoFromSettingItem();
  }

  if (defaultPropertyKey) {
    detailsInfoLabel = getDetailsInfoFromDefaultPropertyRule(
      defaultProperties[defaultPropertyKey],
    );
  }

  const scoreToShow = isSignificant ? score : normalizedScore;

  return (
    <div className="grid grid-cols-[1fr_auto]">
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1">
          {detailsInfoLabel.label}
          {detailConfig?.labelSuffix}
        </span>

        {detailsInfoLabel.comparator && (
          <span className="lowercase font-light ml-1">
            {detailsInfoLabel.comparator}
          </span>
        )}

        {React.isValidElement(detailsInfoLabel.value) ? (
          <span className="ml-1">{detailsInfoLabel.value}</span>
        ) : Array.isArray(detailsInfoLabel.value) ? (
          detailsInfoLabel.value?.map((value, index, array) => (
            <span className="font-semibold ml-1" key={index}>
              {value + (index < array.length - 1 ? ',' : '')}
            </span>
          ))
        ) : (
          <span className="font-semibold ml-1">{detailsInfoLabel.value}</span>
        )}

        {detail.timeUnit && (
          <span className="lowercase ml-1">
            {hashedDateUnits[detail.timeUnit].label}
          </span>
        )}
      </div>

      <div className="flex gap-3 justify-end">
        {detail.eventDate && isValid(new Date(detail.eventDate)) && (
          <span className="text-gray-700 font-medium text-2xs flex items-center">
            {format(new Date(detail.eventDate), 'MMM d, yyyy')}
          </span>
        )}

        <span
          className={cn('font-medium', {
            'text-green-600': significantPositive,
            'text-red-500': significantNegative,
            'text-neutral-500': insignificant,
            'text-sky-700':
              !significantPositive && !significantNegative && !insignificant,
          })}
        >
          {scoreToShow > 0 ? `+${scoreToShow}` : scoreToShow}
        </span>
      </div>
    </div>
  );
};
